// src/components/Profile.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "./Navigation";

// ✅ IMPORTANT: API must point to backend, not frontend.
// Set REACT_APP_API_BASE_URL=http://localhost:3001 in your frontend .env
const API_ORIGIN = process.env.REACT_APP_API_BASE_URL ?? "http://localhost:3001";
const API_BASE = `${API_ORIGIN}/api/v2`;

// Canadian Provinces
const CANADIAN_PROVINCES = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "YT", name: "Yukon" },
];

// US States
const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

export default function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);

  // Form fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [country, setCountry] = useState("CA");

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("token");
      if (!token) {
        setError("Not logged in. Please log in again.");
        setLoading(false);
        return;
      }

      const res = await fetch(`${API_BASE}/auth/profile`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.message || "Failed to load profile");
        setLoading(false);
        return;
      }

      const userData = data.data?.user || data;
      setUser(userData);

      // Populate form fields
      setFirstName(userData.firstName || "");
      setLastName(userData.lastName || "");
      setEmail(userData.email || "");
      setPhone(userData.phone || "");
      setDateOfBirth(userData.dateOfBirth || "");
      setStreetAddress(userData.streetAddress || "");
      setCity(userData.city || "");
      setState(userData.state || "");
      setZipCode(userData.zipCode || "");
      setCountry(userData.country || "CA");

      // Fetch KYC status
      try {
        const kycRes = await fetch(`${API_BASE}/kyc/status`, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
        const kycData = await kycRes.json();
        if (kycData.success) setKycStatus(kycData);
      } catch {}
      try {
        const kycRes = await fetch(`${API_BASE}/kyc/status`, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
        const kycData = await kycRes.json();
        if (kycData.success) setKycStatus(kycData);
      } catch {}
      setLoading(false);
    } catch (err) {
      console.error("Error loading profile:", err);
      setError("Failed to load profile");
      setLoading(false);
    }
  };

  // Phone number formatter - North American 10-digit format
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");
    const limited = cleaned.slice(0, 10);
    return limited;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
  };

  // Display phone with formatting (XXX) XXX-XXXX
  const displayPhone = (phoneNumber) => {
    if (!phoneNumber) return "";
    const cleaned = phoneNumber.replace(/\D/g, "");

    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phoneNumber;
  };

  const handleSave = async (e) => {
    e.preventDefault();

    setError("");
    setSuccess("");
    setSaving(true);

    const token = localStorage.getItem("token");
    if (!token) {
      setError("Not logged in. Please log in again.");
      setSaving(false);
      return;
    }

    // Validate phone number
    if (phone && phone.length !== 10) {
      setError("Phone number must be exactly 10 digits (e.g., 4161234567)");
      setSaving(false);
      return;
    }

    // Only allow updates to non-verified fields
    const updates = {};

    if (!user?.idVerified) {
      // If not ID verified, allow all updates
      updates.firstName = firstName;
      updates.lastName = lastName;
      updates.dateOfBirth = dateOfBirth;
      updates.streetAddress = streetAddress;
      updates.city = city;
      updates.state = state;
      updates.zipCode = zipCode;
      updates.country = country;
    }

    // Phone can always be updated (requires re-verification)
    updates.phone = phone;

    try {
      const res = await fetch(`${API_BASE}/auth/profile`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.message || "Failed to update profile");
        setSaving(false);
        return;
      }

      const updatedUser = { ...(user || {}), ...updates };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      setUser(updatedUser);

      setSuccess("Profile updated successfully!");
      setSaving(false);

      setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      console.error("Error updating profile:", err);
      setError("Failed to update profile");
      setSaving(false);
    }
  };

  const getStateOptions = () => {
    return country === "CA" ? CANADIAN_PROVINCES : US_STATES;
  };

  const getStateLabel = () => {
    return country === "CA" ? "Province" : "State";
  };

  const getZipLabel = () => {
    return country === "CA" ? "Postal Code" : "ZIP Code";
  };

  const isVerified = user?.idVerified || false;
  const isAddressLocked = isVerified;

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="p-6 max-w-3xl mx-auto">
          <p>Loading profile...</p>
        </div>
      </>
    );
  }

  if (error && !user) {
    return (
      <>
        <Navigation />
        <div className="p-6 max-w-3xl mx-auto">
          <div className="bg-red-100 text-red-700 p-4 rounded mb-4">{error}</div>
          <button
            onClick={() => navigate("/login")}
            className="bg-blue-600 text-white px-4 py-2 rounded"
          >
            Go to Login
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="p-6 max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">My Profile</h1>

        {isVerified && (
          <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <span className="text-2xl">✓</span>
              <div className="flex-1">
                <h3 className="font-bold text-green-900 mb-1">Identity Verified</h3>
                <p className="text-sm text-green-800 mb-2">
                  Your identity has been verified. Personal information below is locked for security.
                </p>
                <p className="text-xs text-green-700">
                  Need to update verified information?{" "}
                  <button
                    onClick={() => setShowSupportModal(true)}
                    className="underline font-medium hover:text-green-900"
                  >
                    Contact Support
                  </button>
                </p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-100 text-green-700 p-4 rounded mb-4">✓ {success}</div>
        )}

        {error && <div className="bg-red-100 text-red-700 p-4 rounded mb-4">⚠ {error}</div>}

        <form onSubmit={handleSave} className="space-y-6">
          <div className="border p-4 rounded shadow bg-white">
            <h2 className="font-bold text-lg mb-4">Personal Information</h2>

            {isVerified && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4 text-sm">
                <p className="text-blue-800">
                  🔒 <strong>Protected Fields:</strong> Name, date of birth, and address are locked after identity verification.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  First Name {isVerified && <span className="ml-2 text-xs text-gray-500">🔒 Verified</span>}
                </label>
                <input
                  type="text"
                  className={`border p-2 w-full rounded ${isVerified ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isVerified}
                  readOnly={isVerified}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Last Name {isVerified && <span className="ml-2 text-xs text-gray-500">🔒 Verified</span>}
                </label>
                <input
                  type="text"
                  className={`border p-2 w-full rounded ${isVerified ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isVerified}
                  readOnly={isVerified}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Email (cannot be changed)</label>
                <input
                  type="email"
                  className="border p-2 w-full rounded bg-gray-100 cursor-not-allowed"
                  value={email}
                  disabled
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Phone Number {user?.phoneVerified && <span className="ml-2 text-xs text-green-600">✓ Verified</span>}
                </label>
                <input
                  type="tel"
                  className="border p-2 w-full rounded"
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder="4161234567"
                  maxLength="10"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {phone && phone.length === 10 ? `✓ ${displayPhone(phone)}` : "Enter 10 digits (e.g., 4161234567)"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Date of Birth {isVerified && <span className="ml-2 text-xs text-gray-500">🔒 Verified</span>}
                </label>
                <input
                  type="date"
                  className={`border p-2 w-full rounded ${isVerified ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  disabled={isVerified}
                  readOnly={isVerified}
                />
              </div>
            </div>
          </div>

          <div className="border p-4 rounded shadow bg-white">
            <h2 className="font-bold text-lg mb-4">Address Information</h2>
            <p className="text-sm text-gray-600 mb-4">
              This address will be used as your sender address for all transactions.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Country <span className="text-red-500">*</span>
                  {isAddressLocked && <span className="ml-2 text-xs text-gray-500">🔒 Verified</span>}
                </label>
                <select
                  className={`border p-2 w-full rounded ${isAddressLocked ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value);
                    setState("");
                  }}
                  required
                  disabled={isAddressLocked}
                >
                  <option value="CA">Canada</option>
                  <option value="US">United States</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {country === "CA" ? "🇨🇦 Payment provider: Interac" : "🇺🇸 Payment provider: Sila Money"}
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">
                  Street Address <span className="text-red-500">*</span>
                  {isAddressLocked && <span className="ml-2 text-xs text-gray-500">🔒 Verified</span>}
                </label>
                <input
                  type="text"
                  className={`border p-2 w-full rounded ${isAddressLocked ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  value={streetAddress}
                  onChange={(e) => setStreetAddress(e.target.value)}
                  placeholder="123 Main Street"
                  required
                  disabled={isAddressLocked}
                  readOnly={isAddressLocked}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  City <span className="text-red-500">*</span>
                  {isAddressLocked && <span className="ml-2 text-xs text-gray-500">🔒 Verified</span>}
                </label>
                <input
                  type="text"
                  className={`border p-2 w-full rounded ${isAddressLocked ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Toronto"
                  required
                  disabled={isAddressLocked}
                  readOnly={isAddressLocked}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {getStateLabel()} <span className="text-red-500">*</span>
                  {isAddressLocked && <span className="ml-2 text-xs text-gray-500">🔒 Verified</span>}
                </label>
                <select
                  className={`border p-2 w-full rounded ${isAddressLocked ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  required
                  disabled={isAddressLocked}
                >
                  <option value="">Select {getStateLabel()}</option>
                  {getStateOptions().map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {getZipLabel()} <span className="text-red-500">*</span>
                  {isAddressLocked && <span className="ml-2 text-xs text-gray-500">🔒 Verified</span>}
                </label>
                <input
                  type="text"
                  className={`border p-2 w-full rounded ${isAddressLocked ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  placeholder={country === "CA" ? "M5H 2N2" : "10001"}
                  required
                  disabled={isAddressLocked}
                  readOnly={isAddressLocked}
                />
              </div>
            </div>
          </div>

          <div className="border p-4 rounded shadow bg-white">
            <h2 className="font-bold text-lg mb-4">Identity Verification</h2>
            {kycStatus === null ? (
              <p className="text-sm text-gray-500">Loading verification status...</p>
            ) : kycStatus.status === 'verified' ? (
              <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded p-4">
                <span className="text-2xl">✅</span>
                <div>
                  <p className="font-semibold text-green-800">Identity Verified</p>
                  <p className="text-sm text-gray-600">Document type: {kycStatus.doc_type || 'N/A'}</p>
                  <p className="text-sm text-gray-600">Issuing country: {kycStatus.doc_jurisdiction || 'N/A'}</p>
                  <p className="text-sm text-gray-600">Verified on: {kycStatus.verification_date ? new Date(kycStatus.verification_date).toLocaleDateString() : 'N/A'}</p>
                </div>
              </div>
            ) : kycStatus.status === 'pending' ? (
              <div className="flex items-center gap-3 bg-yellow-50 border border-yellow-200 rounded p-4">
                <span className="text-2xl">⏳</span>
                <div>
                  <p className="font-semibold text-yellow-800">Verification In Progress</p>
                  <p className="text-sm text-gray-600">Your documents are under review.</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded p-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <p className="font-semibold text-red-800">Identity Not Verified</p>
                    <p className="text-sm text-gray-600">You must verify your identity before sending money.</p>
                  </div>
                </div>
                <a href="/kyc" className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700">Verify Now</a>
              </div>
            )}
          </div>

          

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="px-6 py-2 border rounded text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>

      {showSupportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Request Profile Update</h3>
            <p className="text-gray-700 mb-4">
              Your verified information is protected for security reasons. To update your name, date of birth, or address,
              please contact our support team.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
              <p className="text-sm text-blue-900 font-semibold mb-2">Contact Support:</p>
              <p className="text-sm text-blue-800">📧 Email: support@zoompay.co.ke</p>
              <p className="text-sm text-blue-800">📞 Phone: 1-800-ZOOMPAY</p>
              <p className="text-sm text-blue-800 mt-2">⏰ Hours: Mon-Fri 9AM-5PM EST</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowSupportModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Close
              </button>
              <a
                href="mailto:support@zoompay.co.ke?subject=Profile%20Update%20Request"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-center flex items-center justify-center"
              >
                Email Support
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
